import type { CatalogModel, ModeloCobertura } from '@/api/installation.api';

/**
 * EL CATÁLOGO DE MODELOS, COMO UNA SOLA LISTA.
 *
 * En la base son dos: el catálogo de VENTA (`catalogJson`: nombre, precio,
 * fotos, descripción, alias) y el de GARANTÍAS (`trabajosJson.modelos`: los
 * meses de cobertura). Para el dueño siempre fueron lo mismo —un modelo con su
 * precio y su garantía— y tenerlos separados en pantalla tenía un costo
 * concreto: se cargaba el modelo en un lado, no en el otro, y meses después el
 * panel decía «no se puede saber si está en garantía: el modelo no tiene
 * período cargado en Configuración → Trabajos».
 *
 * Acá viven las dos traducciones (unir para editar, separar para guardar) y lo
 * que la pantalla necesita para no obligar a nadie a leer trece filas de
 * formulario: buscar, avisar lo que falta y manejar los alias como fichitas.
 */

/** Una fila del catálogo mientras se edita: los números van como texto. */
export interface ModeloEnEdicion {
  nombre: string;
  /** Texto libre mientras se escribe. Vacío = el modelo no se vende (ver `separarModelos`). */
  precio: string;
  nota: string;
  descripcion: string;
  alias: string[];
  /** Claves de storage de las fotos. Se suben con el botón; nadie las escribe. */
  imagenes: string[];
  /** Meses de cobertura, como texto. Vacío = sin garantía cargada. */
  mesesGarantia: string;
  /** Id de la fila de garantía si ya existía. Vacío = se crea al guardar. */
  idGarantia: string;
}

/** Lo que le falta a un modelo. Se muestra como etiqueta en su tarjeta. */
export type AvisoDeModelo = 'sin foto' | 'sin descripción' | 'sin garantía cargada' | 'sin precio';

/** Texto comparable: sin mayúsculas, sin acentos y sin espacios de más. */
function comparable(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** Id estable para la fila de garantía de un modelo nuevo. */
function idDeGarantia(nombre: string, usados: Set<string>): string {
  const base = `mod-${comparable(nombre)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;
  let id = base;
  let n = 2;
  // Dos modelos distintos pueden dar el mismo id (p. ej. "X 9" y "x-9"): sin
  // esto, el segundo pisaría la cobertura del primero.
  while (usados.has(id)) id = `${base}-${n++}`;
  usados.add(id);
  return id;
}

/**
 * Las dos listas de la base → la lista que se edita en pantalla.
 *
 * Los modelos que solo tienen garantía (DX01: hay artículos instaladas pero su
 * precio nunca se confirmó) también aparecen, al final y sin precio. Si no,
 * nadie podría ver ni corregir su cobertura desde acá.
 */
export function unirModelos(
  catalogo: CatalogModel[],
  modelos: ModeloCobertura[],
): ModeloEnEdicion[] {
  const usados = new Set<ModeloCobertura>();
  const filas: ModeloEnEdicion[] = catalogo.map((m) => {
    const clave = comparable(m.name);
    const cobertura = modelos.find((g) => !usados.has(g) && comparable(g.nombre) === clave);
    if (cobertura) usados.add(cobertura);
    return {
      nombre: m.name,
      precio: String(m.price),
      nota: m.note ?? '',
      descripcion: m.descripcion ?? '',
      alias: [...(m.aliases ?? [])],
      imagenes: [...m.imagenes],
      mesesGarantia: cobertura ? String(cobertura.mesesCobertura) : '',
      idGarantia: cobertura?.id ?? '',
    };
  });

  for (const g of modelos) {
    if (usados.has(g)) continue;
    filas.push({
      nombre: g.nombre,
      precio: '',
      nota: '',
      descripcion: '',
      alias: [],
      imagenes: [],
      mesesGarantia: String(g.mesesCobertura),
      idGarantia: g.id,
    });
  }
  return filas;
}

/**
 * La lista de pantalla → las dos listas que guarda la base.
 *
 * Reglas:
 *  - Sin nombre no hay modelo: la fila se descarta entera (igual que antes).
 *  - Sin precio válido, el modelo NO entra al catálogo de venta, pero conserva
 *    su garantía. Es el caso DX01, y es la razón por la que "sin precio" es un
 *    estado visible y no un 0 puesto en silencio.
 *  - Sin meses no se inventa cobertura: la tarjeta lo avisa.
 */
export function separarModelos(filas: ModeloEnEdicion[]): {
  catalog: CatalogModel[];
  modelos: ModeloCobertura[];
} {
  const catalog: CatalogModel[] = [];
  const modelos: ModeloCobertura[] = [];
  const idsUsados = new Set(filas.map((f) => f.idGarantia).filter(Boolean));

  for (const fila of filas) {
    const nombre = fila.nombre.trim();
    if (nombre === '') continue;

    const precio = parsearPrecio(fila.precio).valor;
    if (precio !== null) {
      catalog.push({
        name: nombre,
        price: precio,
        note: fila.nota.trim() || undefined,
        descripcion: fila.descripcion.trim() || undefined,
        aliases: fila.alias.map((a) => a.trim()).filter(Boolean),
        imagenes: fila.imagenes.filter(Boolean),
      });
    }

    const meses = parsearMeses(fila.mesesGarantia).valor;
    if (meses !== null) {
      modelos.push({
        id: fila.idGarantia || idDeGarantia(nombre, idsUsados),
        nombre,
        mesesCobertura: meses,
      });
    }
  }
  return { catalog, modelos };
}

/**
 * El precio escrito a mano → número.
 *
 * Antes era `parseInt(texto) || 0`: escribir "catorce mil" guardaba RD$0 y la
 * asesora cotizaba en cero sin que nadie se enterara. Ahora un texto que no es
 * un número se dice en pantalla, y vacío significa "sin precio".
 */
export function parsearPrecio(texto: string): { valor: number | null; error: string | null } {
  const limpio = texto.trim();
  if (limpio === '') return { valor: null, error: null };
  // Los puntos y las comas son separadores de miles: "14,995" y "14.995" son
  // las dos formas en que el dueño escribe el mismo precio.
  const soloNumero = limpio.replace(/[.,\s]/g, '');
  if (!/^\d+$/.test(soloNumero)) {
    return { valor: null, error: 'Escribe solo el número, sin letras ni signos.' };
  }
  return { valor: Number(soloNumero), error: null };
}

/** Los meses de garantía escritos a mano → número. Vacío = sin garantía cargada. */
export function parsearMeses(texto: string): { valor: number | null; error: string | null } {
  const limpio = texto.trim();
  if (limpio === '') return { valor: null, error: null };
  if (!/^\d+$/.test(limpio) || Number(limpio) < 1) {
    return {
      valor: null,
      error: 'Escribe cuántos meses dura la garantía (un número mayor que 0).',
    };
  }
  return { valor: Number(limpio), error: null };
}

/**
 * Lo que le falta al modelo, para verlo en la tarjeta.
 *
 * "sin garantía cargada" es el importante: es el aviso que aparece ANTES de que
 * una garantía llegue y no se pueda calcular.
 */
export function avisosDeModelo(fila: ModeloEnEdicion): AvisoDeModelo[] {
  const avisos: AvisoDeModelo[] = [];
  if (parsearPrecio(fila.precio).valor === null) avisos.push('sin precio');
  if (fila.imagenes.filter(Boolean).length === 0) avisos.push('sin foto');
  if (fila.descripcion.trim() === '') avisos.push('sin descripción');
  if (parsearMeses(fila.mesesGarantia).valor === null) avisos.push('sin garantía cargada');
  return avisos;
}

/** Los modelos que coinciden con lo buscado, por nombre o por alias. */
export function filtrarModelos(filas: ModeloEnEdicion[], busqueda: string): ModeloEnEdicion[] {
  const q = comparable(busqueda);
  if (q === '') return filas;
  return filas.filter((f) => [f.nombre, ...f.alias].some((texto) => comparable(texto).includes(q)));
}

/** Agrega un alias recortado. Ignora los vacíos y los repetidos. */
export function agregarAlias(alias: string[], texto: string): string[] {
  const nuevo = texto.trim();
  if (nuevo === '') return alias;
  if (alias.some((a) => comparable(a) === comparable(nuevo))) return alias;
  return [...alias, nuevo];
}

/** Quita un alias de la lista. */
export function quitarAlias(alias: string[], texto: string): string[] {
  return alias.filter((a) => a !== texto);
}
